<script setup>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
  watch
} from 'vue'
import { useStore } from 'vuex'
import Country from './components/Country/index.vue'
import Language from './components/Language/index.vue'
import SearchImg from './components/SearchImg/index.vue'
import { useLoginModal, useLogin } from '@/components/Login'
import HelpCenterAnimate from './components/HelpCenterAnimate/index.vue'
import { fetchData } from '@/utils/fetch'
import { useRoute, useRouter } from 'vue-router'
import {
  buriedPointUtil,
  gaAnalytics,
  openURLWithChannelQuery
} from '@/utils/utils'
import { debounce, throttle } from '../../utils/utils'
import AddressInfoDialog from '@/components/Offer/Sourcing/components/AddressInfoDialog/index.vue'
import { useUserStore } from '@/pinia/user'
import Popover from '@/components/Popover/index.vue'
import Personal from './components/Personal/index.vue'
import { useValidCache } from '@/hooks/useValidCache'
import storage from '@/utils/storage'
import { useLanguageStore } from '@/pinia/language'

const router = useRouter()
const route = useRoute()
const store = useStore()

const { open: openLoginModal } = useLoginModal()

const searchInfo = computed(() => store.state.searchInfo)
const language = useLanguageStore()

const props = defineProps({
  guidancePopVisible: {
    type: Boolean,
    required: false
  }
})

const inputValue = ref('')

const lastQuery = ref('')

const user = useUserStore()

const userInfo = computed(() => user.userInfo || {})

const cartCount = computed(() => store.state.cart.cartCount)

const showHelpCenterAnimate = computed(() => store.state.common.showHelpCenterAnimate)

const handleSearchInput = debounce(() => {
  const complete = localStorage.getItem('complete') || ''
  if (complete.indexOf(5) === -1) {
    store.dispatch('common/openGuidancePop', { id: 5, helpCenterImage: 'https://cdn-image.bestdealer.com/app-bestdealer/1688/guide-image-3.webp' })
  }
}, 300)

const countryObj = ref({})


const handleLogin = () => {
  if (user.isLogged) return

  gaAnalytics('p_sign_in_click', 'p_sign_in_click')
  buriedPointUtil({ eventId: 'PWH201' })
  store.commit('setPassiveLogin', 0)

  openLoginModal()
}

onMounted(() => {
  getCountry()
  getCurrencyList()
  getUserInfo()

  const { keyword = '' } = route.query
  if (keyword) {
    inputValue.value = keyword
    document.title = keyword
  }
})

const countryData = computed(() => store.getters.countryInfo)

const currentCountry = ref(null)
const getCountry = async () => {
  const format = (data) => {
    store.commit('setCountryInfo', data)

    const countryObj = localStorage.getItem('setCurrentCountry')
      ? JSON.parse(localStorage.getItem('setCurrentCountry'))
      : data[0]
    store.commit('setCurrentCountry', countryObj)
    currentCountry.value = countryObj
  }

  const { cacheData, CACHE_VERSION } = useValidCache('cacheCountryList')
  if (cacheData.value) {
    format(cacheData.value.data)
    return
  }
  const { code, data } = await fetchData('/dsp-app/alibaba-common/country')
  if (code === 200) {
    storage.set('cacheCountryList', {
      data,
      timestamp: Date.now(),
      cache_version: CACHE_VERSION
    })
    format(data)
  }
}

const getCurrencyList = async () => {
  const { code, data } = await fetchData('/dsp-app/alibaba-common/currencyList')
  if (code === 200) {
    const _cache = storage.get('currencyData')
    let target
    if (_cache) {
      target = data.find((item) => item.targetCurrency === _cache.targetCurrency)
    } else {
      target = data[0]
    }
    store.commit('setCurrencyList', data)
    store.commit('setCurrencyData', target)
    store.dispatch('setProduct', target)
    storage.set('currencyData', target)
  }
}

const getUserInfo = async () => {
  const { code, data } = await fetchData('/dsp-app/alibaba-inter/queryMyInfo', {
    method: 'post'
  })
  if (code === 200) {
    const completeIds = data?.complete || []
    const complete = localStorage.getItem('complete') || ''

    const strNumbers = complete.split(',').map(num => parseFloat(num)).filter(num => !isNaN(num)) || []
    // 过滤出不在数组中的数字
    const result = strNumbers.filter(num => !completeIds.includes(num))
    if (result.length) {
      const { code } = await fetchData('/dsp-app/alibaba-inter/userComplete', {
        method: 'PUT',
        body: JSON.stringify(result)
      })
      if (code === 200) {
        const completeStr = [...result, ...completeIds].join(',')
        localStorage.setItem('complete', completeStr)
      }
    } else {
      localStorage.setItem('complete', completeIds.join(','))
    }
  }
}

watch(countryData, (newData) => {
  if (newData) {
    countryObj.value = newData
  }
})

const handleChangeItem = throttle((e) => {
  inputValue.value = e.target.value
  if (e.target.value.length >= lastQuery.value.length) {
    lastQuery.value = e.target.value
  }
  if (e.target.value.length < lastQuery.value.length) {
    lastQuery.value = e.target.value
    return
  }
  const searchData = {
    ...searchInfo.value,
    keyword: e.target.value,
    language: language.current,
    pageSize: 50,
    pageNow: 1,
    imageId: 0,
    region: '',
    async: true
  }
  fetchData('/dsp-app/alibaba-common/searchKeyWord?itemNumber=50', {
    method: 'post',
    body: JSON.stringify(searchData)
  })
}, 400)

const handleEnter = async () => {
  try {
    await searchProductList()
  } catch (error) { }
}

const searchProductList = async () => {
  gaAnalytics('p_search_click', 'p_search_click')
  buriedPointUtil({ eventId: 'PSC201' })
  if (!inputValue.value) {
    return
  }

  openURLWithChannelQuery(`/search?keyword=${inputValue.value}&lg=${language.current}`)
}

const goHome = () => {
  location.href = '/'
}

const showAddressInfoDialog = ref(false)

watch(showAddressInfoDialog, (value) => {
  if (value) {
    document.querySelector('body').classList.add('no-scroll')
  } else {
    document.querySelector('body').classList.remove('no-scroll')
  }
})



const toCartPage = () => {
  buriedPointUtil({ eventId: 'PC201' })
  const { href } = router.resolve({
    path: '/carts'
  })
  openURLWithChannelQuery(href)
}

const toOrderPage = () => {
  router.push('/orders')
}

const reloadPage = () => {
  location.reload()
}
</script>

<template>
  <div
    class="search-main"
    @click="openGuidancePop"
  >
    <div class="search-container">
      <div
        class="search-container-logo"
        @click="goHome"
        style="cursor: pointer"
      >
        <!-- <img src="https://cdn-image.bestdealer.com/app-bestdealer/1688/logo.svg" alt="1688" /> -->
        <img
          src="@/assets/header/pc-home-logo.svg"
          alt="1688"
        >
      </div>
      <div
        class="search-input"
        @click="handleSearchInput"
      >
        <Language />
        <input
          type="text"
          class="search-input-item"
          placeholder="🔍 Enter the product description, and let’s find your treasure!"
          @input="handleChangeItem"
          v-model="inputValue"
          @keyup.enter="handleEnter"
        >
        <div class="search-camera">
          <Popover 
            :width="250"
            placement="bottom" 
            trigger="click">
            <template #reference>
              <img src="@/assets/images/camera.svg" alt="">
            </template>
            <SearchImg/>
          </Popover>
        </div>
        <div
          class="search-btn"
          @click="searchProductList"
        >
          <img
            src="https://cdn-image.bestdealer.com/app-bestdealer/1688/search-icon.svg"
            alt=""
          >
        </div>
      </div>
      <div class="search-country">
        <div class="country-left">
          <img
            :src="countryData[0]?.countryUrl"
            alt=""
          >
        </div>
        <Popover placement="bottom" trigger="hover">
          <template #reference>
            <div class="country-right">
              <div class="country-text">
                {{ countryData[0]?.countryName }}
              </div>
              <div class="country-unit">
                <span>{{ countryData[0]?.countryCode }}</span>
                <img
                  src="@/assets/select-down.svg"
                  alt=""
                >
              </div>
            </div>
          </template>
          <Country />
        </Popover>
      </div>
      <div
        @click="toCartPage"
        class="search-shopping-cart"
      >
        <div class="cart-icon-wrapper">
          <img
            v-if="cartCount"
            calss="cart-icon"
            src="@/assets/header/cart-with-sku.svg"
            alt=""
          >
          <img
            v-else
            class="cart-icon"
            src="@/assets/header/cart-empty.svg"
            alt=""
          >
        </div>
        <div class="cart-content">
          <div class="cart-count">
            {{ cartCount > 99 ? '99+' : cartCount }}
          </div>
          <div class="cart-text">
            Cart
          </div>
        </div>
      </div>
      <div
        @click="toOrderPage"
        class="search-shopping-order"
      >
        <img
          src="@/assets/images/order.svg"
          alt=""
        >
      </div>
      <!-- 个人中心 -->
      <Popover 
        :width="240"
        :disabled="!userInfo?.name"
        placement="bottom" 
        trigger="hover">
        <template #reference>
          <div 
            class="search-person"
            :class="{ showPerson: showHelpCenterAnimate }"
            @click="handleLogin">
            <div
              class="person-left"
              :class="{ personLeftZoom: showHelpCenterAnimate }"
            >
              <img
                src="https://cdn-image.bestdealer.com/app-bestdealer/1688/person.svg"
                alt=""
                style="cursor: pointer"
              >
            </div>
            <div class="person-right">
              <div class="person-text">
                Hello,
                <template v-if="!userInfo?.name">
                  Sign In
                </template>
              </div>
              <div class="person-account">
                <template v-if="userInfo?.name">
                  {{ userInfo?.name.length > 4
                    ? userInfo?.name.slice(0, 4) + "***"
                    : userInfo?.name
                  }}
                </template>
                <template v-else>
                  Account
                </template>
              </div>
            </div>
          </div>
        </template>
        <Personal @showAddressInfoDialog="showAddressInfoDialog = true" />
      </Popover>          
      <HelpCenterAnimate v-if="showHelpCenterAnimate" />
    </div>

    <AddressInfoDialog
      v-if="showAddressInfoDialog"
      @save-address="reloadPage"
      :address-type="3"
      :current-country="currentCountry"
      :handle-close="() => showAddressInfoDialog = false"
    />
  </div>
</template>

<style scoped lang="less">
.search-main {
  width: 100vw;
  height: 72px;
  padding: 4px 0px;
  background-color: #052784;
}

.search-main .search-container {
  width: 1200px;
  margin: auto;
  display: flex;
  align-items: center;
  height: 72px;

  .search-container-logo {
    margin-right: 24px;
  }
}

.search-input {
  position: relative;
  margin-right: 24px;
}

.search-input .search-camera {
  position: absolute;
  top: 48%;
  transform: translateY(-50%);
  right: 64px;
  width: 26px;
  height: 26px;
  z-index: 101;

  img {
    cursor: pointer;
  }
}

.search-input .search-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 4px;
  width: 48px;
  height: 32px;
  background-color: #f57231;
  border-radius: 16px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.search-input .search-input-item {
  width: 600px;
  height: 40px;
  box-sizing: border-box;
  padding-right: 106px;
  border-radius: 20px;
  border: none;
  font-size: 14px;
  padding-left: 88px;
  outline: none;
  font-family: mon-500;

  &::placeholder {
    color: #B2B7C2;
  }
}

.search-input .search-input-item:hover {
  outline: 1px solid #0069e0;
}

.search-input .search-input-item:active {
  outline: 1px solid #0069e0;
}

input::placeholder {
  font-size: 12px;
  font-family: mon-500;
  line-height: 16px;
  color: #6e717a;
}

.search-country {
  margin-left: auto;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 98px;
  margin-right: 16px;
}

.search-country .country-left {
  width: 40px;
  height: 40px;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;

  img {
    padding: 4px;
    width: 100%;
    object-fit: cover;
    display: flex;
  }
}

.search-country .country-right {
  cursor: pointer;
}

.search-country .country-right .country-text {
  font-family: mon-500;
  font-size: 12px;
  line-height: 18px;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-country .country-right .country-unit {
  font-family: mon-600;
  font-size: 12px;
  line-height: 18px;
  color: #ffffff;
}

.search-country .country-right .country-unit img {
  vertical-align: middle;
  margin-left: 4px;
}

.search-shopping-cart {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
  cursor: pointer;
  .cart-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    transition: background .3s ease-in-out;
    &:hover {
      background-color: #ffffff16;
    }
    .cart-icon {
      width: 40px;
      height: 40px;
    }
  }
  .cart-content {
    .cart-count {
      width: 28px;
      height: 14px;
      border-radius: 8px;
      background-color: #009de0;
      border: 1px solid white;
      color: white;
      font-size: 10px;
      font-family: mon-500;
      text-align: center;
      line-height: 14px;
      margin-top: 4px;
    }
    .cart-text {
      margin-top: 3px;
      color: white;
      font-size: 12px;
      font-family: mon-600;
      line-height: 18px;
    }
  }
}

.search-shopping-order {
  margin-right: 12px;
  display: flex;
  align-items: center;
  cursor: pointer;

  > img {
    width: 40px;
    height: 40px;
  }
}

.search-person {
  display: flex;
  align-items: center;
  min-width: 138px;
  position: relative;

  .person-left {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
  }

  .personLeftZoom {
    animation: zoomInOut 0.8s ease-in-out 0.4s infinite;
  }
}

@keyframes zoomInOut {
  0% {
    transform: scale(1);
    /* 初始大小 */
  }

  50% {
    transform: scale(1.2);
    /* 放大到1.5倍 */
  }

  100% {
    transform: scale(1);
    /* 缩小回原始大小 */
  }
}

.showPerson {
  z-index: 1200;
}

.search-person .person-right {
  margin-left: 8px;
  cursor: pointer;
}

.search-person .person-right .person-text {
  font-size: 12px;
  font-family: mon-500;
  line-height: 18px;
  color: #ffffff;
}

.search-person .person-right .person-account {
  font-size: 12px;
  font-family: mon-600;
  line-height: 18px;
  color: #ffffff;
}
</style>
